import { add, differenceInMinutes } from "date-fns";
import _ from "lodash";
import { ITransfer, ITransferTask, IVirtualTrip, TransferTaskType } from "../../../common/plannerTypes";
import { ISchedule, ITransferTasksEstimate, TransferEstimateTaskType } from "../../../common/types";
import {
  ITimelineCommand,
  ITransferEstimate,
  ITransferTaskEstimateVM,
  ITripPlannerBatchRequest,
  ITripPlannerEstimateRequest,
  ITripPlannerEstimateResponse,
  IVirtualTripUpdate,
  TimelineCommandType,
  TripPlannerTransferModel,
  VirtualTripModel,
} from "../types/plannerTypes";
import { getDateTimeStrForBackEnd } from "./plannerHelpers";
import store from "../../../store/appStore";

const moveTransferToFirstPosition = (firstTransferId: string, transfers: ITransfer[]): ITransfer[] => {
  if (transfers[0].transferId !== firstTransferId) {
    const index = transfers.findIndex((t) => t.transferId === firstTransferId);
    const firstTransfer = transfers.splice(index, 1)[0];
    return [firstTransfer, ...transfers];
  }
  return transfers;
};

const syncVirtualTripWithTransfers = (virtualTrip: IVirtualTrip) => {
  // move transfer to first position regarding first task
  if (virtualTrip?.transferTasks[0]?.transferId !== virtualTrip?.transfers[0]?.transferId){
    virtualTrip.transfers = moveTransferToFirstPosition(virtualTrip.transferTasks[0].transferId, virtualTrip.transfers);
    virtualTrip.virtualTripId = virtualTrip.transfers[0].transferId;
    virtualTrip.isMultiLoaded = virtualTrip.transfers.length > 1;
  }
  else{
    virtualTrip.isMultiLoaded = virtualTrip.transfers.length > 1;
  }
};

const copyVirtualTripTransfer = (transfer: ITransfer) => {
  return {
    transferId: transfer.transferId,
    scheduleObj: {
      ...transfer.scheduleObj,
    },
  };
};

const copyVirtualTripTransferTask = (transferTask: ITransferTask) => ({ ...transferTask });

export const copyVirtualTrip = (currentTrip: IVirtualTrip): IVirtualTrip => {
  const transfersCopy = currentTrip.transfers.map((tr) => copyVirtualTripTransfer(tr));
  const transferTasksCopy = currentTrip.transferTasks.map((tt) => copyVirtualTripTransferTask(tt));
  const virtualTrip: IVirtualTrip = { ...currentTrip, transfers: transfersCopy, transferTasks: transferTasksCopy };
  return virtualTrip;
};

const fromITransferTaskEstimateVMToIITransferTaskEstimate = (
  transferId: string,
  virtualTripId: string,
  taskVM: ITransferTaskEstimateVM
): ITransferTasksEstimate => {
  return {
    transferTasksEstimateId: null,
    sequence: null,
    transferId: transferId,
    startDateTime: taskVM.startTime,
    customerLocationtimezone: taskVM.customerLocationtimezone,
    endDateTime: taskVM.completedTime,
    duration: taskVM.duration,
    type: taskVM.type,
    virtualTripId: virtualTripId,
  };
};

const mapITransferTaskEstimateToITransferTask = (task: ITransferTasksEstimate): ITransferTask => {
  let taskType: TransferTaskType;
  switch (task.type) {
    case TransferEstimateTaskType.DropingOff:
      taskType = TransferTaskType.dropoff;
      break;
    case TransferEstimateTaskType.PickingUp:
      taskType = TransferTaskType.pickup;
      break;
    default:
      throw Error("Transfer task type not supported");
  }
  return {
    transferId: task.transferId,
    startTime: new Date(task.startDateTime as string),
    customerLocationtimezone: task.customerLocationtimezone,
    duration: task.duration,
    taskType: taskType,
  };
};

const mapFromIScheduleToITransfer = (scheduleObj: ISchedule): ITransfer => {
  return {
    transferId: scheduleObj.transferId,
    scheduleObj: scheduleObj,
  };
};

const fromIScheduleToITransferTasks = (scheduleObj: ISchedule): ITransferTask[] => {
  const emptyPickup = {
    transferId: scheduleObj.transferId,
    taskType: TransferTaskType.pickup,
    duration: null,
    startTime: null,
    customerLocationtimezone : scheduleObj.customerLocationtimezone,
  };
  const emptyDropoff = {
    transferId: scheduleObj.transferId,
    taskType: TransferTaskType.dropoff,
    duration: null,
    startTime: null,
    customerLocationtimezone : scheduleObj.customerLocationtimezone,
  };

  if ((scheduleObj.transferTaskEstimates?.length ?? 0) === 0) {
    return [emptyPickup, emptyDropoff];
  }

  let pickupTask: ITransferTask | null = null;
  let dropoffTask: ITransferTask | null = null;

  // to avoid doubles
  for (const tte of scheduleObj.transferTaskEstimates!!) {
    if (tte.transferId !== scheduleObj.transferId) continue;

    if (!pickupTask && tte.type === TransferEstimateTaskType.PickingUp) {
      pickupTask = mapITransferTaskEstimateToITransferTask(tte);
      continue;
    }
    if (!dropoffTask && tte.type === TransferEstimateTaskType.DropingOff) {
      dropoffTask = mapITransferTaskEstimateToITransferTask(tte);
      continue;
    }
  }
  return [pickupTask ?? emptyPickup, dropoffTask ?? emptyDropoff];
};

const mapFromIScheduleToSingleVirtualTrip = (scheduleObj: ISchedule): IVirtualTrip => {
  const estimatedDuration = scheduleObj.estimatedDurationInMunutes ? scheduleObj.estimatedDurationInMunutes : 5;
  const overrideEstimatedDuration = scheduleObj.isTripMinOverrided == true ? scheduleObj.overrideTripEstimateInMin : (scheduleObj.estimatedDurationInMunutes ? scheduleObj.estimatedDurationInMunutes : 5);
  return {
    virtualTripId: scheduleObj.transferId,
    startTrip: new Date(scheduleObj.scheduledDate as string),
    endTrip: add(new Date(scheduleObj.scheduledDate as string), { minutes: estimatedDuration }),
    overridedendTrip: add(new Date(scheduleObj.scheduledDate as string), { minutes: overrideEstimatedDuration }),
    actualEndTrip: add(new Date(scheduleObj.scheduledDate as string), { minutes: estimatedDuration }),
    tripPlannerId: scheduleObj.status === 2|| scheduleObj.isPlannerScheduled ? scheduleObj.tripPlannerId : null,
    statusVT: scheduleObj.status,
    isMultiLoaded: false,
    transfers: [mapFromIScheduleToITransfer(scheduleObj)],
    transferTasks: fromIScheduleToITransferTasks(scheduleObj),
    deletedFromTimeLine: false,
    isTripMinOverrided:scheduleObj.isTripMinOverrided ?? false,
    overrideTripEstimateInMin: scheduleObj.overrideTripEstimateInMin ?? 0,
  };
};

const mapFromVirtualTripModelToVirtualTrip = (model: VirtualTripModel,schedule:ISchedule[]): IVirtualTrip => {
  var isTripMinOverrided = schedule[0].isTripMinOverrided ?? false;
  var overrideTripEstimateInMin = schedule[0].overrideTripEstimateInMin ?? 0;
  var duration  = model.estimatedDurationInMinutes
  var overrideDuration  = (isTripMinOverrided == true ? overrideTripEstimateInMin : model.estimatedDurationInMinutes)
  return {
    virtualTripId: model.virtualTripId,
    tripPlannerId: model.tripPlannerId,
    statusVT: 0,
    transfers: [],
    transferTasks: [],
    startTrip: new Date(model.scheduledDate as string),
    endTrip: add(new Date(model.scheduledDate as string), { minutes: duration }),
    overridedendTrip: add(new Date(model.scheduledDate as string), { minutes: overrideDuration }),
    actualEndTrip: add(new Date(model.scheduledDate as string), { minutes: duration }),
    isMultiLoaded: true,
    deletedFromTimeLine: false,
    isTripMinOverrided:isTripMinOverrided,
    overrideTripEstimateInMin: overrideTripEstimateInMin,
  };
};

const mapVirtualTripToVirtualTripModel = (trip: IVirtualTrip, locationId: string): VirtualTripModel => {
  var endtrip  = trip.isTripMinOverrided == true ? trip.overridedendTrip : trip.endTrip;
  return {
    virtualTripId: trip.virtualTripId,
    scheduledDate: getDateTimeStrForBackEnd(trip.startTrip),
    locationId: locationId,
    tripPlannerId: trip.tripPlannerId ?? null,
    estimatedDurationInMinutes: differenceInMinutes(endtrip, trip.startTrip)
  };
};

const applyCommandToVirtualTrip = (droppedTrip: IVirtualTrip, timelineCommand: ITimelineCommand): IVirtualTripUpdate => {
  const result: IVirtualTripUpdate = {
    resultTrip: copyVirtualTrip(droppedTrip),
    freedTripAfterRemoving: null,
    ignoreResultTrip: false,
  };
  switch (timelineCommand.type) {
    case TimelineCommandType.moveTrip:
      result.resultTrip.startTrip = timelineCommand.plannerMinute!!.date;
      result.resultTrip.endTrip = timelineCommand.plannerMinute!!.date;
      result.resultTrip.tripPlannerId = timelineCommand.plannerMinute!!.tripPlannerId;
      break;
    case TimelineCommandType.addToTrip:   
      result.resultTrip = copyVirtualTrip(timelineCommand.tripUpdates!!);
      result.resultTrip.transfers = [...result.resultTrip.transfers, ...droppedTrip.transfers.map((tr) => copyVirtualTripTransfer(tr))];
      result.resultTrip.transferTasks = [
        ...result.resultTrip.transferTasks,
        ...droppedTrip.transferTasks.map((tt) => copyVirtualTripTransferTask(tt)),
      ];
      break;
    case TimelineCommandType.removeFromTrip:    
      const freedTransferIndex = result.resultTrip.transfers.findIndex((tr) => tr.transferId === timelineCommand.trasferIdForRemoving);
      if (freedTransferIndex !== -1) {
        const freedTransfer = copyVirtualTripTransfer(result.resultTrip.transfers[freedTransferIndex]);
        result.resultTrip.transfers.splice(freedTransferIndex, 1);
        result.resultTrip.transferTasks = result.resultTrip.transferTasks.filter((tt) => tt.transferId !== timelineCommand.trasferIdForRemoving);
        result.ignoreResultTrip = result.resultTrip.transfers.length === 0;
        freedTransfer.scheduleObj.status = 1;
        result.freedTripAfterRemoving = mapFromIScheduleToSingleVirtualTrip(freedTransfer.scheduleObj);
        result.freedTripAfterRemoving.tripPlannerId = null;
        result.freedTripAfterRemoving.statusVT = 1;
        result.freedTripAfterRemoving.isTripMinOverrided = false;
        result.freedTripAfterRemoving.overrideTripEstimateInMin = 0;
        result.resultTrip.isTripMinOverrided = false;
        result.resultTrip.overrideTripEstimateInMin = 0;
        result.resultTrip.overridedendTrip = result.resultTrip.endTrip;
        if(droppedTrip.virtualTripId == timelineCommand.trasferIdForRemoving)
          {
            let _remainingTrips = droppedTrip.transfers.filter(x=>x.transferId != timelineCommand.trasferIdForRemoving);
            if(_remainingTrips.length >0)
              {
                result.resultTrip.virtualTripId = _remainingTrips[0].transferId;
              }          
          }
      }
      break;
    case TimelineCommandType.applyTransferOrderChanges:
      if (timelineCommand.tripUpdates!!.transferTasks.length !== result.resultTrip.transferTasks.length) {
        throw "the lenght of new and old trip must be the same for applyTransferOrderChanges";
      }
      result.resultTrip.transferTasks = [...timelineCommand.tripUpdates!!.transferTasks.map((tt) => copyVirtualTripTransferTask(tt))];
      break;
    case TimelineCommandType.overrideTrip:
      if (timelineCommand.tripUpdates!!.transferTasks.length !== result.resultTrip.transferTasks.length) {
        throw "the lenght of new and old trip must be the same for applyTransferOrderChanges";
      }
      result.resultTrip.transferTasks = [...timelineCommand.tripUpdates!!.transferTasks.map((tt) => copyVirtualTripTransferTask(tt))];
      result.resultTrip.isTripMinOverrided = timelineCommand?.tripUpdates?.isTripMinOverrided ?? false
      result.resultTrip.overrideTripEstimateInMin = timelineCommand?.tripUpdates?.overrideTripEstimateInMin ?? 0
      break;
    default:
      break;
  }
  if (!result.ignoreResultTrip) {
    syncVirtualTripWithTransfers(result.resultTrip);
  }
  return result;
};

const applyVirtualTripEstimate = (virtualTripRequest: IVirtualTrip, estimateResponse: ITripPlannerEstimateResponse): IVirtualTrip => {

  const virtualTrip = copyVirtualTrip(virtualTripRequest);
  virtualTrip.startTrip = new Date(estimateResponse.tripStartTime as string);
  virtualTrip.endTrip = new Date(estimateResponse.tripEndTime as string);
  virtualTrip.overridedendTrip = new Date(estimateResponse.tripOverridedEndTime as string);
  virtualTrip.isTripMinOverrided = estimateResponse.isTripMinOverrided;
    for (const transfer of virtualTrip.transfers) {
        const transferUpdate = estimateResponse.transferEstimates.find((q) => q.transferId === transfer.transferId)!!;
        // update scheduleObj
        transfer.scheduleObj.scheduledDate = transferUpdate.transferStart;
        transfer.scheduleObj.customerLocationtimezone = transferUpdate.customerLocationtimezone;
        transfer.scheduleObj.tripPlannerId = virtualTripRequest.tripPlannerId;
        transfer.scheduleObj.estimatedDurationInMunutes = estimateResponse.tripDuration;
        transfer.scheduleObj.transferTaskEstimates = transferUpdate.taskEstimates.map((te) =>
        fromITransferTaskEstimateVMToIITransferTaskEstimate(transfer.transferId, virtualTrip.virtualTripId, te)
         );
  }

  if (virtualTrip.transfers.length === 1) {
    virtualTrip.transferTasks = fromIScheduleToITransferTasks(virtualTrip.transfers[0].scheduleObj);
  } 
  else {
    const orderedTransferTasks = _.orderBy(
      virtualTrip.transfers.map((t) => t.scheduleObj).flatMap((sch) => fromIScheduleToITransferTasks(sch)),
      "startTime"
    );
    virtualTrip.transferTasks = orderedTransferTasks;
  }

  return virtualTrip;
};

const createTripPlannerEstimateRequest = (virtualTripRequest: IVirtualTrip, locationId: string): ITripPlannerEstimateRequest => {
  const transferEstimates: ITransferEstimate[] = virtualTripRequest.transferTasks.map((task) => ({
    transferId: task.transferId,
    type: task.taskType,
  }));

  return {
    virtualTripId: virtualTripRequest.virtualTripId,
    scheduleDate: getDateTimeStrForBackEnd(virtualTripRequest.startTrip),
    locationId: locationId,
    tripPlannerId: virtualTripRequest.tripPlannerId,
    transferEstimates: transferEstimates,
    transfers: virtualTripRequest.transfers.map((t) => t.transferId),
    isTripMinOverrided:virtualTripRequest.isTripMinOverrided,
    overrideTripEstimateInMin:virtualTripRequest.overrideTripEstimateInMin
  };
};

const combineIScheduleAndVisualTrips = (
  scheduleRequestedList: ISchedule[],
  preparedScheduleScheduledForTransportList: ISchedule[],
  visualTripModels: VirtualTripModel[]
): IVirtualTrip[] => {
  const emptyVirtualTripId = "singleTrip";
  // find multiload schedules
  const groupedISchedules = _.groupBy(preparedScheduleScheduledForTransportList, (item) => {
    const virtualTripIdValue = item["virtualTripId"];
    if (!virtualTripIdValue) {
      return emptyVirtualTripId;
    }
    return virtualTripIdValue;
  });
  const multiloadTrips: IVirtualTrip[] = [];
  const singleTrips: IVirtualTrip[] = [...scheduleRequestedList.map(mapFromIScheduleToSingleVirtualTrip)];
  for (const key in groupedISchedules) {
    const schedules = groupedISchedules[key];
    if (key === emptyVirtualTripId || schedules.length === 1) {
      singleTrips.push(...schedules.map(mapFromIScheduleToSingleVirtualTrip));
      continue;
    }
    const foundModel = visualTripModels.find((m) => m.virtualTripId === key);
    if (!foundModel) {
      singleTrips.push(...schedules.map(mapFromIScheduleToSingleVirtualTrip));
      continue;
    } else {
      const multiloadTrip = mapFromVirtualTripModelToVirtualTrip(foundModel,schedules);
      const orderedTransferTasks = _.orderBy(
        schedules.flatMap((sch) => fromIScheduleToITransferTasks(sch)),
        "startTime"
      );
      multiloadTrip.transferTasks = orderedTransferTasks;
      multiloadTrip.transfers = moveTransferToFirstPosition(orderedTransferTasks[0].transferId, schedules.map(mapFromIScheduleToITransfer));
      
      multiloadTrip.statusVT = multiloadTrip.transfers[0].scheduleObj.status;
      multiloadTrips.push(multiloadTrip);
    }
  }
  
  return [...singleTrips, ...multiloadTrips];
};

const prepareOldTripDeletetingObj = (oldTrip: IVirtualTrip, newTrip: IVirtualTrip): IVirtualTrip | null => {
  if (oldTrip.virtualTripId === newTrip.virtualTripId) return null;

  // if initially trip came from backend with status scheduled, then this trip should dissapear from timeline
  // which means be in local state with status deletedFromTimeLine
  const foundScheduledTrip = store.appStore.getState().plannerStore.globalState.find((v) => v.statusVT === 2);
  if (foundScheduledTrip) {
    const copyOldTrip = copyVirtualTrip(oldTrip);
    copyOldTrip.deletedFromTimeLine = true;
    copyOldTrip.tripPlannerId = newTrip.tripPlannerId;
    return copyOldTrip;
  }

  return null;
};

const prepareNewLocalState = (
  localState: IVirtualTrip[],
  droppedTrip: IVirtualTrip,
  anotherOldVirtualTrip: IVirtualTrip | null,
  updatedVirtualTrip: IVirtualTrip
) => {
  // delete/mark deleted all related to a command objects from local state
  const deletetedOldTrips: IVirtualTrip[] = [];
  // in some cases droppedTrip and initial trip can be equal, because of command structure
  if (anotherOldVirtualTrip && droppedTrip.virtualTripId !== anotherOldVirtualTrip.virtualTripId) {
    const deletedTrip = prepareOldTripDeletetingObj(droppedTrip, updatedVirtualTrip);
    if (deletedTrip) {
      deletetedOldTrips.push(deletedTrip);
    }
  }
  if (anotherOldVirtualTrip && updatedVirtualTrip.virtualTripId !== anotherOldVirtualTrip.virtualTripId) {
    const deletedTrip = prepareOldTripDeletetingObj(anotherOldVirtualTrip, updatedVirtualTrip);
    if (deletedTrip) {
      deletetedOldTrips.push(deletedTrip);
    }
  }

  // exclude all trips above (with old states)
  const newLocalStateRequest = localState.filter(
    (v) =>
      v.virtualTripId !== droppedTrip.virtualTripId &&
      v.virtualTripId !== anotherOldVirtualTrip?.virtualTripId &&
      v.virtualTripId !== updatedVirtualTrip.virtualTripId
  );

  // add updated virtual trip to local state
  newLocalStateRequest.push(updatedVirtualTrip, ...deletetedOldTrips);
  return newLocalStateRequest;
};

const fromStringToDateTimeStrForBackEnd = (dateStr: string): string => getDateTimeStrForBackEnd(new Date(dateStr));

const mapTransferToTripPlannerTransferModel = (transfer: ITransfer, trip: IVirtualTrip): TripPlannerTransferModel => {
  const isItMultiloadScheduledTransfer = trip.tripPlannerId && trip.isMultiLoaded;
  return {
    transferId: transfer.transferId,
    tripPlannerId: trip.tripPlannerId,
    status: trip.tripPlannerId ? 2 : 1,
    scheduledDate: transfer.scheduleObj.scheduledDate ? fromStringToDateTimeStrForBackEnd(transfer.scheduleObj.scheduledDate) : null,
    estimatedDurationInMinutes: transfer.scheduleObj.estimatedDurationInMunutes,
    virtualTripId: isItMultiloadScheduledTransfer ? trip.virtualTripId : null,
    isTripMinOverrided: trip.isTripMinOverrided ?? false,
    overrideTripEstimateInMin: trip.overrideTripEstimateInMin ?? 0,
  };
};

const prepareTripPlannerBatchRequest = (locationId: string, selectedDay: string): ITripPlannerBatchRequest => {
  const localState = store.appStore.getState().plannerStore.localState;
  const requestModel: ITripPlannerBatchRequest = {
    virtualTrips: [],
    transfers: [],
    selectedLocationId: locationId,
    selectedDay: selectedDay,
  };
  const tripsForSaving = localState.filter((q) => !q.deletedFromTimeLine);
  for (const trip of tripsForSaving) {
    if (trip.isMultiLoaded) {
      requestModel.virtualTrips.push(mapVirtualTripToVirtualTripModel(trip, locationId));
      const transferModels = trip.transfers.map((tr, index) => mapTransferToTripPlannerTransferModel(tr, trip));
      requestModel.transfers.push(...transferModels);
    } else {
      requestModel.transfers.push(mapTransferToTripPlannerTransferModel(trip.transfers[0], trip));
    }
  }
  return requestModel;
};

const getLastVersionVisualState = (): IVirtualTrip[] => store.appStore.getState().plannerStore.visualState;

const getLastVersionGlobalState = (): IVirtualTrip[] => store.appStore.getState().plannerStore.globalState;

export {
  mapFromIScheduleToSingleVirtualTrip,
  createTripPlannerEstimateRequest,
  applyCommandToVirtualTrip,
  applyVirtualTripEstimate,
  combineIScheduleAndVisualTrips,
  prepareNewLocalState,
  getLastVersionVisualState,
  getLastVersionGlobalState,
  prepareTripPlannerBatchRequest,
};
